/**
 * HeatmapApp.jsx — Root component for the SAGD Heatmap Dashboard.
 */

import { useState, useCallback, useEffect, useRef } from "react";

import TopBar        from "./components/TopBar.jsx";
import UploadStep    from "./components/UploadStep.jsx";
import PipelineStage from "./components/PipelineStage.jsx";
import LiveDocsTable from "./components/LiveDocsTable.jsx";
import HeatmapGrid   from "./components/HeatmapGrid.jsx";
import LogConsole    from "./components/LogConsole.jsx";
import ColumnStats   from "./components/ColumnStats.jsx";

import { useSSE } from "./useSSE.js";
import { api }    from "./api.js";

const EMPTY_STEPS = {
  scrape: { done: 0, total: 0, pct: 0, status: "idle", rate: 0, eta_min: null },
  enrich: { done: 0, total: 0, pct: 0, status: "idle", rate: 0, eta_min: null },
  score:  { done: 0, total: 0, pct: 0, status: "idle", rate: 0, eta_min: null },
};

export default function HeatmapApp() {
  const [uploadInfo,     setUploadInfo]     = useState(null);
  const [progress,       setProgress]       = useState({
    filename: "", total_docs: 0, total_questions: 0, steps: EMPTY_STEPS,
  });
  const [colStats,       setColStats]       = useState({ stats: {}, enriched_total: 0 });
  const [tableItems,     setTableItems]     = useState([]);
  const tableIndex     = useRef({});
  const tableItemsRef  = useRef([]);
  useEffect(() => { tableItemsRef.current = tableItems; }, [tableItems]);

  const [hmDocs,   setHmDocs]   = useState([]);
  const [hmQs,     setHmQs]     = useState([]);
  const [hmScores, setHmScores] = useState({});
  const hmDocIndex = useRef({});

  const [logs,          setLogs]          = useState([]);
  const [health,        setHealth]        = useState(null);
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [resetting,     setResetting]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saveToast,     setSaveToast]     = useState("");
  const [history,       setHistory]       = useState([]);
  const [exportStatus,  setExportStatus]  = useState("idle"); // "idle"|"building"|"ready"

  const pushLog = useCallback((msg, ts) => {
    const now = ts || new Date().toLocaleTimeString("en-GB");
    setLogs((prev) => [{ ts: now, msg }, ...prev].slice(0, 500));
  }, []);

  // ── Heatmap data refresh (reads disk, always up-to-date) ──────────
  const refreshHeatmapData = useCallback(() => {
    api.getHeatmapPreview(50).then(({ docs, questions, scores }) => {
      if (!docs.length) return;
      docs.forEach((d) => { hmDocIndex.current[d.doc_no] = 0; });
      setHmDocs(docs);
      setHmScores(scores);
      if (questions.length > 0) {
        setHmQs(questions);
      } else {
        // Reconstruct question list from score keys across all returned docs
        const seen = new Set();
        const qs = [];
        for (const doc of docs) {
          for (const q of Object.keys(scores[doc.doc_no] || {})) {
            if (!seen.has(q)) { seen.add(q); qs.push(q); }
          }
        }
        if (qs.length) setHmQs(qs);
      }
    }).catch(() => {});
  }, []);

  // ── SSE handler (stable — no tableItems in deps) ──────────────────
  const handleSSE = useCallback((event) => {
    const { type } = event;

    if (event.progress) {
      setProgress((prev) => mergeProgress(prev, event.progress));
    }
    if (type === "init" || type === "heartbeat") return;

    if (type === "progress") {
      const { step, done, total, pct, rate, eta_min } = event;
      setProgress((prev) => ({
        ...prev,
        steps: { ...prev.steps, [step]: { ...prev.steps[step], done, total, pct, rate, eta_min } },
      }));
      return;
    }

    if (type === "status_change") {
      setProgress((prev) => ({
        ...prev,
        steps: { ...prev.steps, [event.step]: { ...prev.steps[event.step], status: event.status } },
      }));
      return;
    }

    if (type === "log") {
      pushLog(event.msg, event.ts);
      return;
    }

    if (type === "reset") {
      setUploadInfo(null);
      setProgress({ filename: "", total_docs: 0, total_questions: 0, steps: EMPTY_STEPS });
      setTableItems([]); setHmDocs([]); setHmQs([]); setHmScores({});
      setColStats({ stats: {}, enriched_total: 0 });
      tableIndex.current = {}; hmDocIndex.current = {};
      return;
    }

    if (type === "scrape_done") {
      const { doc_no, url, ok } = event;
      setTableItems((prev) => {
        if (tableIndex.current[doc_no] !== undefined) return prev;
        tableIndex.current[doc_no] = 0;
        return [{ doc_no, url, enrichment_done: false, scoring_done: false, scrape_ok: ok }, ...prev].slice(0, 5000);
      });
      return;
    }

    if (type === "enrich_done") {
      const {
        doc_no, url,
        // original user-supplied fields
        date_added, document_type, question_bank, evidence, trl,
        // LLM-enriched fields (all non-null after _normalize_info)
        title, pub_date, author, summary, category, secondary_cat,
        relevance, subtopics, companies, use_cases, grant, bd, sourcing,
        tech, operators, trusted,
      } = event;

      const updated = {
        doc_no, url: url || "",
        enrichment_done: true, scoring_done: false,
        date_added, document_type, question_bank, evidence, trl,
        title, pub_date, author, summary, category, secondary_cat,
        relevance, subtopics, companies, use_cases, grant, bd, sourcing,
        tech, operators, trusted,
      };

      setTableItems((prev) => {
        const exists = prev.some((r) => r.doc_no === doc_no);
        if (exists) return prev.map((r) => r.doc_no === doc_no ? { ...r, ...updated } : r);
        tableIndex.current[doc_no] = 0;
        return [updated, ...prev].slice(0, 5000);
      });
      api.getColumnStats().then(setColStats).catch(() => {});
      return;
    }

    if (type === "score_done") {
      const { doc_no, scores } = event;
      setTableItems((prev) =>
        prev.map((r) => r.doc_no === doc_no ? { ...r, scoring_done: true } : r)
      );
      setHmScores((prev) => ({ ...prev, [doc_no]: scores }));
      setHmDocs((prev) => {
        if (hmDocIndex.current[doc_no] !== undefined) return prev;
        const title = tableItemsRef.current.find((r) => r.doc_no === doc_no)?.title || `Doc ${doc_no}`;
        hmDocIndex.current[doc_no] = 0;
        return [{ doc_no, title }, ...prev].slice(0, 30);
      });
      // Populate question rows from score keys if hmQs is still empty
      // (handles page refresh where Redis questions list may be gone)
      const scoreKeys = Object.keys(scores || {});
      if (scoreKeys.length > 0) {
        setHmQs((prev) => {
          if (prev.length >= scoreKeys.length) return prev;
          // Merge preserving order — add any question not yet in the list
          const existing = new Set(prev);
          const merged   = [...prev];
          for (const q of scoreKeys) {
            if (!existing.has(q)) merged.push(q);
          }
          return merged;
        });
      }
      return;
    }

    if (type === "step_complete") {
      setProgress((prev) => ({
        ...prev,
        steps: { ...prev.steps, [event.step]: { ...prev.steps[event.step], status: "done" } },
      }));
      pushLog(`✓ ${event.step} phase complete`);
      // When scoring finishes, force a fresh disk-read to populate the grid
      // (in case any score_done SSE events were missed during reconnects)
      if (event.step === "score") refreshHeatmapData();
      return;
    }

    if (type === "pipeline_complete") {
      pushLog("🎉 Full pipeline complete! Click 'Download Processed Excel' to download.");
      setRunAllLoading(false);
      refreshHeatmapData();
      // Backend is pre-building Excel — start polling for ready status
      setExportStatus("building");
      api.prepareExport().catch(() => {});
      return;
    }
  }, [pushLog, refreshHeatmapData]);

  useSSE(handleSSE);

  // ── Bootstrap ─────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const safe = (fn) => fn().catch(() => {});

    safe(() => api.getLogs(150).then(({ logs: raw }) => {
      if (alive && raw.length) setLogs(raw.slice().reverse());
    }));
    safe(() => api.health().then((h) => alive && setHealth(h)));
    safe(() => api.getProgress().then((p) => {
      if (!alive) return;
      setProgress(p);
      // Restore uploadInfo after a page refresh so UploadStep shows the
      // "file loaded" view (with Run/Reset buttons) instead of the dropzone
      if (p.total_docs > 0) {
        setUploadInfo((prev) => prev || {
          filename:        p.filename || "previous upload",
          total_docs:      p.total_docs,
          total_questions: p.total_questions,
          sample_urls:     [],
        });
      }
    }));
    safe(() => api.getResults(5000).then(({ items }) => {
      if (alive && items.length) {
        setTableItems(items);
        items.forEach((r) => { tableIndex.current[r.doc_no] = 0; });
      }
    }));
    safe(() => api.getHeatmapPreview(30).then(({ docs, questions, scores }) => {
      if (!alive || !docs.length) return;
      setHmDocs(docs);
      setHmScores(scores);
      docs.forEach((d) => { hmDocIndex.current[d.doc_no] = 0; });

      // If backend returned questions use them; otherwise reconstruct from score keys
      // (Redis may have been cleared — backend rebuilds from disk but may miss order)
      if (questions.length > 0) {
        setHmQs(questions);
      } else {
        // Collect all unique question keys from every scored doc's scores object
        const seen = new Set();
        const rebuilt = [];
        for (const doc of docs) {
          const docScores = scores[doc.doc_no] || {};
          for (const q of Object.keys(docScores)) {
            if (!seen.has(q)) { seen.add(q); rebuilt.push(q); }
          }
        }
        if (rebuilt.length > 0) setHmQs(rebuilt);
      }
    }));
    safe(() => api.getColumnStats().then((cs) => alive && setColStats(cs)));
    safe(() => api.getHistory().then(({ history: h }) => alive && setHistory(h || [])));

    // Restore export status on page load
    safe(() => api.getExportStatus().then(({ status }) => alive && setExportStatus(status || "idle")));

    const healthTimer = setInterval(() => { api.health().then(setHealth).catch(() => {}); }, 15_000);
    const colTimer    = setInterval(() => { api.getColumnStats().then(setColStats).catch(() => {}); }, 10_000);

    return () => { alive = false; clearInterval(healthTimer); clearInterval(colTimer); };
  }, []);

  useEffect(() => {
    // Re-fetch heatmap data whenever docs have been scored but the grid is still empty
    const needsRefresh = hmDocs.length === 0 && progress.steps?.score?.done > 0;
    const needsQs      = hmQs.length === 0 && (hmDocs.length > 0 || progress.total_questions > 0);
    if (!needsRefresh && !needsQs) return;
    refreshHeatmapData();
  }, [progress.total_questions, progress.steps?.score?.done, hmQs.length, hmDocs.length, refreshHeatmapData]);

  // Periodic poll while scoring is running — catches missed SSE events
  useEffect(() => {
    if (progress.steps?.score?.status !== "running") return;
    const t = setInterval(refreshHeatmapData, 8000);
    return () => clearInterval(t);
  }, [progress.steps?.score?.status, refreshHeatmapData]);

  // Poll export build status until ready
  useEffect(() => {
    if (exportStatus !== "building") return;
    const t = setInterval(() => {
      api.getExportStatus().then(({ status }) => {
        setExportStatus(status || "idle");
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [exportStatus]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleUploaded = useCallback((result) => {
    setUploadInfo(result);
    setProgress((prev) => ({
      ...prev,
      filename:        result.filename,
      total_docs:      result.total_docs,
      total_questions: result.total_questions,
      steps:           EMPTY_STEPS,
    }));
    setTableItems([]); setHmDocs([]); setHmQs([]); setHmScores({});
    setColStats({ stats: {}, enriched_total: 0 });
    tableIndex.current = {}; hmDocIndex.current = {};
    pushLog(`📁 Uploaded: ${result.filename} — ${result.total_docs} docs, ${result.total_questions} questions`);
    api.getHistory().then(({ history: h }) => setHistory(h || [])).catch(() => {});
  }, [pushLog]);

  const handleRunAll = useCallback(async () => {
    if (!uploadInfo) return;
    setRunAllLoading(true);
    try {
      await api.startAll();
      pushLog("▶ Full pipeline started: scrape → enrich → score");
    } catch (e) {
      pushLog(`Error: ${e.message}`);
      setRunAllLoading(false);
    }
  }, [uploadInfo, pushLog]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    try {
      await api.reset();
      setUploadInfo(null);
      setProgress({ filename: "", total_docs: 0, total_questions: 0, steps: EMPTY_STEPS });
      setTableItems([]); setHmDocs([]); setHmQs([]); setHmScores({});
      setColStats({ stats: {}, enriched_total: 0 });
      setLogs([]); setExportStatus("idle");
      tableIndex.current = {}; hmDocIndex.current = {};
      pushLog("♻️ Data reset — upload a new file to begin");
    } catch (e) {
      pushLog(`Reset error: ${e.message}`);
    } finally {
      setResetting(false);
    }
  }, [pushLog]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveToast("");
    try {
      const res = await api.saveSession();
      setSaveToast(`✓ Saved ${res.doc_count} docs → ${res.snapshot}`);
      pushLog(`💾 Session saved: ${res.snapshot}`);
      // Clear all data after successful save so a fresh upload can begin
      await handleReset();
      setTimeout(() => setSaveToast(""), 6000);
    } catch (e) {
      setSaveToast(`✗ Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [pushLog, handleReset]);

  const { steps, total_docs, total_questions } = progress;
  const hasUpload  = total_docs > 0;

  // allDone: accept status==="done" OR filesystem-derived counts (survive server restarts)
  const _stepsDone = (s) => steps[s].status === "done" ||
    (steps[s].done > 0 && steps[s].total > 0 && steps[s].done >= steps[s].total);
  const allDone = hasUpload && ["scrape","enrich","score"].every(_stepsDone);

  // Show Save+Export bar when any enrichment results exist (table or progress counter)
  const hasAnyEnriched = tableItems.some((r) => r.enrichment_done) || steps.enrich.done > 0 || colStats.enriched_total > 0;
  const hasAnyScored   = tableItems.some((r) => r.scoring_done)    || steps.score.done  > 0;

  const enrichDone  = steps.enrich.status === "done" || steps.enrich.done > 0;
  const anyRunning  = Object.values(steps).some((s) => s.status === "running");

  // Export: available when all done OR any scoring data exists (data survives restarts)
  const showExport  = allDone || (hasUpload && hasAnyScored);
  const showSaveBar = enrichDone || allDone || hasAnyEnriched;

  return (
    <>
      <TopBar progress={progress} health={health} />

      <div className="hm-page">

        {/* ── Metric banner ─────────────────────────────────────── */}
        <div className="hm-metric-banner">
          <MetricCard
            value={total_docs.toLocaleString()}
            label="Total Docs"
            sub={total_questions ? `${total_questions} questions` : "Upload Excel to begin"}
          />
          <MetricCard
            value={steps.scrape.done.toLocaleString()}
            label="Scraped"
            sub={`${steps.scrape.pct.toFixed(1)}% · ${steps.scrape.status}`}
            color="#1E90FF" pct={steps.scrape.pct}
          />
          <MetricCard
            value={steps.enrich.done.toLocaleString()}
            label="Enriched"
            sub={`${steps.enrich.pct.toFixed(1)}% · ${steps.enrich.status}`}
            color="#1987C9" pct={steps.enrich.pct}
          />
          <MetricCard
            value={steps.score.done.toLocaleString()}
            label="Scored"
            sub={`${steps.score.pct.toFixed(1)}% · ${steps.score.status}`}
            color="#DB2777" pct={steps.score.pct}
          />
          <MetricCard
            value={overallPct(steps, total_docs) + "%"}
            label="Overall Progress"
            sub={overallSub(steps)}
            color={allDone ? "#0F7E6D" : undefined}
            pct={overallPct(steps, total_docs)}
          />
        </div>

        {/* ── Pipeline stages ───────────────────────────────────── */}
        <div className="hm-stages-row">
          <UploadStep
            onUploaded={handleUploaded}
            uploadInfo={uploadInfo}
            onRunAll={handleRunAll}
            onReset={handleReset}
            runAllLoading={runAllLoading}
            resetting={resetting}
            anyRunning={anyRunning}
          />
          <PipelineStage step="scrape" stepData={steps.scrape} disabled={!hasUpload} />
          <PipelineStage step="enrich" stepData={steps.enrich} disabled={!hasUpload} />
          <PipelineStage step="score"  stepData={steps.score}  disabled={!hasUpload} />
        </div>

        {/* ── Upload History ────────────────────────────────────── */}
        {history.length > 0 && <HistoryPanel history={history} />}

        {/* ── Column stats ──────────────────────────────────────── */}
        {colStats.enriched_total > 0 && (
          <ColumnStats stats={colStats.stats} enrichedTotal={colStats.enriched_total} />
        )}

        {/* ── Save / Export action bar ──────────────────────────── */}
        {showSaveBar && (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Save button: available once any doc is enriched */}
            <button
              className="hm-btn hm-btn--ghost"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "⏳ Saving…" : "💾 Save Session"}
            </button>

            {/* Export button: shown when any scoring data exists */}
            {showExport && (
              exportStatus === "building" ? (
                <span className="hm-btn hm-btn--success"
                  style={{ opacity: 0.7, cursor: "default" }}>
                  ⏳ Preparing Excel…
                </span>
              ) : (
                <a
                  href={api.exportUrl()}
                  className="hm-btn hm-btn--success"
                  download="SAGD_processed_output.xlsx"
                  style={{ textDecoration: "none" }}
                  onClick={() => { if (exportStatus !== "ready") setExportStatus("building"); }}
                >
                  {exportStatus === "ready" ? "📥 Download Ready" : "📥 Download Excel"}
                </a>
              )
            )}

            {saveToast && (
              <span style={{
                fontSize: 12,
                color: saveToast.startsWith("✓") ? "#0F7E6D" : "#EF4444",
                background: saveToast.startsWith("✓") ? "#0F7E6D1A" : "#EF44441A",
                border: `1px solid ${saveToast.startsWith("✓") ? "#0F7E6D44" : "#EF444444"}`,
                borderRadius: 6,
                padding: "4px 10px",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                {saveToast}
                <button
                  onClick={() => setSaveToast("")}
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: "inherit", fontSize: 14, lineHeight: 1, padding: 0 }}
                >×</button>
              </span>
            )}

            <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              {showExport
                ? "Sheet 1: Enriched Data (25 cols) · Sheet 2: Heatmap (Q × URL)"
                : "Complete heatmap scoring to enable export"}
            </span>
          </div>
        )}

        {/* ── Live docs table ───────────────────────────────────── */}
        <LiveDocsTable items={tableItems} total={total_docs} />

        {/* ── Heatmap grid ──────────────────────────────────────── */}
        <HeatmapGrid
          docs={hmDocs}
          questions={hmQs}
          scores={hmScores}
          totalScored={steps.score.done}
          totalDocs={total_docs}
          onRefresh={refreshHeatmapData}
        />

        {/* ── Log console ───────────────────────────────────────── */}
        <LogConsole logs={logs} />
      </div>
    </>
  );
}

// ── Upload history panel ───────────────────────────────────────────
function HistoryPanel({ history }) {
  return (
    <section className="hm-section" style={{ marginBottom: 12 }}>
      <div className="hm-section-header">
        <h2 className="hm-section-title" style={{ fontSize: 13 }}>Upload History</h2>
        <span className="hm-section-badge">{history.length}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {history.slice(0, 10).map((h, i) => (
          <div key={i} style={{
            background: "var(--surface-secondary)",
            border: "1px solid var(--divider-color-light)",
            borderRadius: 6, padding: "6px 12px", fontSize: 11,
          }}>
            <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{h.filename}</div>
            <div style={{ color: "var(--text-tertiary)" }}>
              {h.total_docs} docs · {h.total_questions} questions ·{" "}
              {h.timestamp ? new Date(h.timestamp * 1000).toLocaleString() : ""}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Helpers ────────────────────────────────────────────────────────
function mergeProgress(prev, next) {
  if (!next) return prev;
  return { ...prev, ...next, steps: { ...prev.steps, ...(next.steps || {}) } };
}

function overallPct(steps, total) {
  if (!total) return 0;
  const sum = (steps.scrape?.done || 0) + (steps.enrich?.done || 0) + (steps.score?.done || 0);
  return Math.round(sum / (total * 3) * 100);
}

function overallSub(steps) {
  const running = ["scrape","enrich","score"].find((s) => steps[s]?.status === "running");
  if (!running) {
    const paused = ["scrape","enrich","score"].find((s) => steps[s]?.status === "paused");
    return paused ? `⏸ ${paused} paused` : "Idle — upload Excel to begin";
  }
  const s = steps[running];
  if (s.rate > 0 && s.eta_min != null)
    return `${s.rate} docs/min · ETA ${s.eta_min < 1 ? "< 1m" : s.eta_min + "m"}`;
  return `${running.charAt(0).toUpperCase() + running.slice(1)} running…`;
}

function MetricCard({ value, label, sub, color, pct }) {
  return (
    <div className="hm-metric-card">
      <div className="hm-metric-card__value" style={color ? { color } : {}}>{value}</div>
      <div className="hm-metric-card__label">{label}</div>
      {sub && <div className="hm-metric-card__sub">{sub}</div>}
      {pct != null && pct > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="hm-progress-bar" style={{ height: 4, marginBottom: 0 }}>
            <div className="hm-progress-bar__fill"
              style={{ width: `${Math.min(pct, 100)}%`, background: color || "var(--category-color-focus)" }} />
          </div>
        </div>
      )}
    </div>
  );
}
